import {
  collection,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  and,
  Timestamp,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  CrewShift,
  BreakRecord,
  CrewStatus,
  CrewHandoverEvent,
  User,
  Role,
} from '../types';
import { logAuditAction } from './auditService';
import { userDocRef } from './firestorePaths';

/**
 * Crew Management Service
 * Handles crew sign-on/off, shifts, breaks, status updates, handovers
 */

export interface ShiftReadinessOverride {
  enabled: boolean;
  reason?: string;
  approvedByUid?: string;
  approvedByName?: string;
}

export interface ShiftReadinessInput {
  hasRotaAssignment: boolean;
  vehicleReady: boolean;
  kitReady: boolean;
  deviceReady: boolean;
  override?: ShiftReadinessOverride;
}

export interface ShiftReadinessResult {
  ready: boolean;
  overridden: boolean;
  blockers: string[];
}

export interface CrewSignOnMetadata {
  additionalCrewMembers?: Array<{
    userId: string;
    name: string;
    role: Role;
    employeeId?: string;
  }>;
  plannedShiftStart?: string;
  plannedShiftEnd?: string;
  odometerStart?: number;
  vdiCompleted?: boolean;
  vdiNotes?: string;
}

export type ShiftSignOffDiscrepancyType = 'ControlledDrug' | 'Asset';

export interface ShiftSignOffDiscrepancy {
  id: string;
  type: ShiftSignOffDiscrepancyType;
  referenceId?: string;
  description: string;
  severity: 'Low' | 'Medium' | 'High';
  resolved?: boolean;
  resolutionNotes?: string;
}

export interface ShiftSignOffOverride {
  enabled: boolean;
  reason?: string;
  approvedByUid?: string;
  approvedByName?: string;
}

export interface ShiftSignOffReconciliationInput {
  controlledDrugCheckCompleted: boolean;
  assetCheckCompleted: boolean;
  discrepancies: ShiftSignOffDiscrepancy[];
  notes?: string;
  override?: ShiftSignOffOverride;
}

export interface ShiftSignOffReconciliationResult {
  ready: boolean;
  overridden: boolean;
  blockers: string[];
  discrepancyCount: number;
  unresolvedCount: number;
}

const toAuditMetadataString = (values: string[]): string | null => {
  if (values.length === 0) {
    return null;
  }

  return values.join(' | ');
};

/**
 * Evaluate shift-start prerequisites before allowing dispatch availability.
 */
export const evaluateShiftStartReadiness = (
  input: ShiftReadinessInput
): ShiftReadinessResult => {
  const blockers: string[] = [];

  if (!input.hasRotaAssignment) blockers.push('No active rota assignment for this shift window.');
  if (!input.vehicleReady) blockers.push('Assigned vehicle check not current or vehicle unavailable.');
  if (!input.kitReady) blockers.push('Medical kit readiness check failed or overdue.');
  if (!input.deviceReady) blockers.push('Device readiness/pairing checks are incomplete.');

  if (blockers.length === 0) {
    return {
      ready: true,
      overridden: false,
      blockers: [],
    };
  }

  if (input.override?.enabled) {
    return {
      ready: true,
      overridden: true,
      blockers,
    };
  }

  return {
    ready: false,
    overridden: false,
    blockers,
  };
};

/**
 * Validate end-of-shift reconciliation prior to sign-off.
 */
export const evaluateShiftSignOffReconciliation = (
  input: ShiftSignOffReconciliationInput
): ShiftSignOffReconciliationResult => {
  const blockers: string[] = [];

  if (!input.controlledDrugCheckCompleted) {
    blockers.push('Controlled drug end-of-shift check must be completed.');
  }
  if (!input.assetCheckCompleted) {
    blockers.push('Vehicle/kit discrepancy check must be completed.');
  }

  const discrepancyCount = input.discrepancies.length;
  const unresolvedCount = input.discrepancies.filter((entry) => entry.resolved !== true).length;

  if (unresolvedCount > 0 && !input.override?.enabled) {
    blockers.push('Unresolved discrepancies require supervisor override before sign-off.');
  }

  if (input.override?.enabled) {
    if (!input.override.approvedByName?.trim() && !input.override.approvedByUid?.trim()) {
      blockers.push('Supervisor override requires approver identity.');
    }
    if (!input.override.reason?.trim()) {
      blockers.push('Supervisor override requires a reason.');
    }
  }

  const ready = blockers.length === 0;
  return {
    ready,
    overridden: ready && input.override?.enabled === true,
    blockers,
    discrepancyCount,
    unresolvedCount,
  };
};

// ─── Sign-On / Sign-Off ────────────────────────────────────────────────────

/**
 * Crew member signs on to a shift
 * Validates: crew is rostered, vehicle available, PIN correct
 */
export async function crewSignOn(
  tenantId: string,
  crewMemberId: string,
  crewMemberName: string,
  crewMemberRole: Role,
  vehicleId: string,
  vehicleCallSign: string,
  readiness?: ShiftReadinessInput,
  metadata?: CrewSignOnMetadata
): Promise<CrewShift> {
  // Check if crew already has active shift
  const activeShifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', crewMemberId),
        where('shiftEnd', '==', null)
      )
    )
  );

  if (activeShifts.size > 0) {
    throw new Error('Crew member already has an active shift. Sign off first.');
  }

  if (!readiness) {
    throw new Error('Shift readiness gate input is required before crew sign-on.');
  }

  const readinessResult = evaluateShiftStartReadiness(readiness);
  if (!readinessResult.ready) {
    throw new Error(`Shift readiness gate blocked sign-on: ${readinessResult.blockers.join(' ')}`);
  }

  // Create new shift
  const now = new Date().toISOString();
  const crewShift: CrewShift = {
    id: '', // Will be set by Firestore
    tenantId,
    crewMemberId,
    crewMemberName,
    crewMemberRole,
    additionalCrewMembers: metadata?.additionalCrewMembers || [],
    vehicleId,
    vehicleCallSign,
    shiftStart: now,
    plannedShiftStart: metadata?.plannedShiftStart,
    plannedShiftEnd: metadata?.plannedShiftEnd,
    odometerStart: metadata?.odometerStart,
    vdiCompleted: metadata?.vdiCompleted,
    vdiNotes: metadata?.vdiNotes,
    status: CrewStatus.Available,
    breaks: [],
    handovers: [],
  };

  const docRef = await addDoc(collection(db, 'crew_shifts'), crewShift);
  crewShift.id = docRef.id;

  // Log audit event
  let readinessAuditSuffix = '';
  if (readinessResult.overridden) {
    const overrideBy = readiness.override?.approvedByName || readiness.override?.approvedByUid || 'Supervisor';
    readinessAuditSuffix = ` | Override applied by ${overrideBy}: ${readiness.override?.reason || 'No reason recorded'} | Blockers: ${readinessResult.blockers.join(' ')}`;
  } else {
    readinessAuditSuffix = ' | Readiness gate passed (rota, vehicle, kit, device).';
  }

  await logAuditAction(
    crewMemberId,
    crewMemberName,
    'CrewSignOn',
    `Crew sign-on: ${crewMemberName} (${crewMemberRole}) - Vehicle: ${vehicleCallSign}${readinessAuditSuffix}`,
    'Workforce',
    {
      tenantId,
      targetType: 'CrewShift',
      targetId: crewShift.id,
      metadata: {
        readinessPassed: readinessResult.ready,
        readinessOverridden: readinessResult.overridden,
        readinessBlockerCount: readinessResult.blockers.length,
        readinessBlockers: toAuditMetadataString(readinessResult.blockers),
        hasRotaAssignment: readiness.hasRotaAssignment,
        vehicleReady: readiness.vehicleReady,
        kitReady: readiness.kitReady,
        deviceReady: readiness.deviceReady,
        overrideReason: readiness.override?.reason?.trim() || null,
        overrideApprovedByUid: readiness.override?.approvedByUid?.trim() || null,
        overrideApprovedByName: readiness.override?.approvedByName?.trim() || null,
        additionalCrewCount: metadata?.additionalCrewMembers?.length || 0,
        plannedShiftStart: metadata?.plannedShiftStart || null,
        plannedShiftEnd: metadata?.plannedShiftEnd || null,
        odometerStart: metadata?.odometerStart ?? null,
        vdiCompleted: metadata?.vdiCompleted ?? null,
        vdiNotes: metadata?.vdiNotes?.trim() || null,
      },
      severity: readinessResult.overridden ? 'Warning' : 'Info',
    }
  );

  console.log(`✓ Crew signed on: ${crewMemberName} at ${now}`);
  return crewShift;
}

/**
 * Crew member signs off from active shift
 */
export async function crewSignOff(
  tenantId: string,
  crewMemberId: string,
  signOffReason?: string,
  reconciliation?: ShiftSignOffReconciliationInput,
  crewMemberName?: string
): Promise<void> {
  const now = new Date().toISOString();

  // Find active shift
  const activeShifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', crewMemberId),
        where('shiftEnd', '==', null)
      )
    )
  );

  if (activeShifts.size === 0) {
    throw new Error('No active shift found for crew member');
  }

  if (!reconciliation) {
    throw new Error('End-of-shift reconciliation is required before sign-off.');
  }

  const reconciliationResult = evaluateShiftSignOffReconciliation(reconciliation);
  if (!reconciliationResult.ready) {
    throw new Error(`Sign-off reconciliation blocked: ${reconciliationResult.blockers.join(' ')}`);
  }

  const shiftDoc = activeShifts.docs[0];
  const shift = shiftDoc.data() as CrewShift;

  // Calculate break minutes
  const totalBreakMinutes = shift.breaks.reduce((sum, b) => {
    return sum + (b.durationMinutes || 0);
  }, 0);

  // Calculate on-scene minutes (approximation from incident timestamps)
  const totalOnSceneMinutes = 0; // Would be calculated from linked incidents

  // Update shift
  await updateDoc(doc(db, 'crew_shifts', shiftDoc.id), {
    shiftEnd: now,
    status: CrewStatus.OffDuty,
    totalBreakMinutes,
    totalOnSceneMinutes,
    signedOffBy: crewMemberId,
    signedOffAt: now,
    signOffReason: signOffReason || null,
    reconciliation: {
      ...reconciliation,
      completedAt: now,
      completedByUid: crewMemberId,
      completedByName: crewMemberName || shift.crewMemberName || 'Unknown',
      summary: {
        discrepancyCount: reconciliationResult.discrepancyCount,
        unresolvedCount: reconciliationResult.unresolvedCount,
        overridden: reconciliationResult.overridden,
      },
    },
  });

  if (reconciliation.discrepancies.length > 0) {
    await Promise.all(
      reconciliation.discrepancies.map((entry) =>
        addDoc(collection(db, 'shift_reconciliation_discrepancies'), {
          tenantId,
          shiftId: shiftDoc.id,
          crewMemberId,
          crewMemberName: crewMemberName || shift.crewMemberName || 'Unknown',
          recordedAt: now,
          signOffReason: signOffReason || null,
          ...entry,
        })
      )
    );
  }

  // Log audit
  await logAuditAction(
    crewMemberId,
    crewMemberName || shift.crewMemberName || 'Unknown',
    'Crew Sign-off',
    `Crew sign-off. Total breaks: ${totalBreakMinutes}min. Discrepancies: ${reconciliationResult.discrepancyCount} (unresolved: ${reconciliationResult.unresolvedCount}). Reason: ${signOffReason || 'N/A'}`,
    'Workforce',
    {
      tenantId,
      targetType: 'CrewShift',
      targetId: shiftDoc.id,
      metadata: {
        discrepancies: reconciliationResult.discrepancyCount,
        unresolvedDiscrepancies: reconciliationResult.unresolvedCount,
        reconciliationOverridden: reconciliationResult.overridden,
        reconciliationBlockerCount: reconciliationResult.blockers.length,
        reconciliationBlockers: toAuditMetadataString(reconciliationResult.blockers),
        controlledDrugCheckCompleted: reconciliation.controlledDrugCheckCompleted,
        assetCheckCompleted: reconciliation.assetCheckCompleted,
        overrideReason: reconciliation.override?.reason?.trim() || null,
        overrideApprovedByUid: reconciliation.override?.approvedByUid?.trim() || null,
        overrideApprovedByName: reconciliation.override?.approvedByName?.trim() || null,
        signOffReason: signOffReason?.trim() || null,
      },
      severity: reconciliationResult.unresolvedCount > 0 ? 'Warning' : 'Info',
    }
  );

  console.log(`✓ Crew signed off at ${now}`);
}

// ─── Status Updates ────────────────────────────────────────────────────────

/**
 * Update crew shift status (e.g., Available → Mobile → OnScene → Transporting)
 */
export async function updateCrewStatus(
  tenantId: string,
  crewMemberId: string,
  newStatus: CrewStatus,
  incidentId?: string,
  assignmentId?: string
): Promise<void> {
  const now = new Date().toISOString();

  // Find active shift
  const activeShifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', crewMemberId),
        where('shiftEnd', '==', null)
      )
    )
  );

  if (activeShifts.size === 0) {
    throw new Error('No active shift found');
  }

  const shiftDoc = activeShifts.docs[0];
  const shift = shiftDoc.data() as CrewShift;

  // Update status
  const updateData: any = {
    status: newStatus,
  };

  if (incidentId) {
    updateData.currentIncidentId = incidentId;
  }
  if (assignmentId) {
    updateData.currentAssignmentId = assignmentId;
  }

  await updateDoc(doc(db, 'crew_shifts', shiftDoc.id), updateData);

  // Log audit
  await logAuditAction(
    crewMemberId,
    shift.crewMemberName || 'Unknown',
    'Crew Status Updated',
    `Status changed: ${newStatus}${incidentId ? ` (Incident: ${incidentId})` : ''}`,
    'Operational',
    {
      tenantId,
      targetType: 'CrewShift',
      targetId: shiftDoc.id,
      metadata: {
        status: newStatus,
        incidentId: incidentId || null,
        assignmentId: assignmentId || null,
      },
    }
  );

  console.log(`✓ Crew status updated to: ${newStatus}`);
}

// ─── Breaks ────────────────────────────────────────────────────────────────

/**
 * Start a break for crew member
 */
export async function startBreak(
  tenantId: string,
  crewMemberId: string,
  breakType: 'Meal' | 'Rest' | 'Admin' | 'Other' = 'Rest',
  reason?: string
): Promise<BreakRecord> {
  const now = new Date().toISOString();

  // Find active shift
  const activeShifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', crewMemberId),
        where('shiftEnd', '==', null)
      )
    )
  );

  if (activeShifts.size === 0) {
    throw new Error('No active shift found');
  }

  const shiftDoc = activeShifts.docs[0];
  const shift = shiftDoc.data() as CrewShift;

  // Check if already on break
  const activeBreak = shift.breaks.find((b) => !b.endTime);
  if (activeBreak) {
    throw new Error('Crew member already on break. End current break first.');
  }

  // Create break record
  const breakRecord: BreakRecord = {
    id: Date.now().toString(),
    startTime: now,
    type: breakType,
    reason,
  };

  // Add to shifts array
  const updatedBreaks = [...shift.breaks, breakRecord];
  await updateDoc(doc(db, 'crew_shifts', shiftDoc.id), {
    breaks: updatedBreaks,
    status: CrewStatus.OnBreak,
  });

  console.log(`✓ Break started: ${breakType}`);
  return breakRecord;
}

/**
 * End crew member's break
 */
export async function endBreak(
  tenantId: string,
  crewMemberId: string
): Promise<number> {
  const now = new Date().toISOString();

  // Find active shift
  const activeShifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', crewMemberId),
        where('shiftEnd', '==', null)
      )
    )
  );

  if (activeShifts.size === 0) {
    throw new Error('No active shift found');
  }

  const shiftDoc = activeShifts.docs[0];
  const shift = shiftDoc.data() as CrewShift;

  // Find active break
  const activeBreakIndex = shift.breaks.findIndex((b) => !b.endTime);
  if (activeBreakIndex === -1) {
    throw new Error('No active break found');
  }

  // Calculate break duration
  const activeBreak = shift.breaks[activeBreakIndex];
  const startTime = new Date(activeBreak.startTime);
  const endTime = new Date(now);
  const durationMinutes = Math.round(
    (endTime.getTime() - startTime.getTime()) / 1000 / 60
  );

  // Update break record
  const updatedBreaks = [...shift.breaks];
  updatedBreaks[activeBreakIndex] = {
    ...activeBreak,
    endTime: now,
    durationMinutes,
  };

  // Update shift (status back to Available if not on incident)
  const newStatus = shift.currentIncidentId
    ? CrewStatus.Mobile
    : CrewStatus.Available;

  await updateDoc(doc(db, 'crew_shifts', shiftDoc.id), {
    breaks: updatedBreaks,
    status: newStatus,
  });

  // Warn if break < 20 minutes
  if (durationMinutes < 20) {
    console.warn(
      `⚠️ Break was only ${durationMinutes}min (recommended: 20min per 6h work)`
    );
  }

  console.log(`✓ Break ended: ${durationMinutes} minutes`);
  return durationMinutes;
}

// ─── Handovers ────────────────────────────────────────────────────────────

/**
 * Create handover event when crew responsibility changes
 */
export async function createHandoverEvent(
  tenantId: string,
  fromCrewMemberId: string,
  toCrewMemberId: string,
  role: Role,
  incidentId?: string,
  details?: string
): Promise<CrewHandoverEvent> {
  const now = new Date().toISOString();

  // Get user details
  const fromUserDoc = await getDoc(userDocRef(db, fromCrewMemberId, tenantId));
  const toUserDoc = await getDoc(userDocRef(db, toCrewMemberId, tenantId));

  if (!fromUserDoc.exists() || !toUserDoc.exists()) {
    throw new Error('One or both crew members not found');
  }

  const fromUser = fromUserDoc.data() as User;
  const toUser = toUserDoc.data() as User;

  // Create handover event
  const handoverEvent: CrewHandoverEvent = {
    id: Date.now().toString(),
    timestamp: now,
    fromCrewMemberId,
    fromCrewMemberName: fromUser.name,
    toCrewMemberId,
    toCrewMemberName: toUser.name,
    role,
    currentIncidentId: incidentId,
    details,
  };

  // Add to both crew members' shifts
  const shiftCollectionRef = collection(db, 'crew_shifts');

  // Find both active shifts and update them
  const activeShiftsFrom = await getDocs(
    query(
      shiftCollectionRef,
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', fromCrewMemberId),
        where('shiftEnd', '==', null)
      )
    )
  );

  const activeShiftsTo = await getDocs(
    query(
      shiftCollectionRef,
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', toCrewMemberId),
        where('shiftEnd', '==', null)
      )
    )
  );

  if (activeShiftsFrom.size > 0) {
    const fromShift = activeShiftsFrom.docs[0].data() as CrewShift;
    const updatedHandoversFrom = [
      ...fromShift.handovers,
      handoverEvent,
    ];
    await updateDoc(doc(db, 'crew_shifts', activeShiftsFrom.docs[0].id), {
      handovers: updatedHandoversFrom,
    });
  }

  if (activeShiftsTo.size > 0) {
    const toShift = activeShiftsTo.docs[0].data() as CrewShift;
    const updatedHandoversTo = [...toShift.handovers, handoverEvent];
    await updateDoc(doc(db, 'crew_shifts', activeShiftsTo.docs[0].id), {
      handovers: updatedHandoversTo,
    });
  }

  // Log audit
  await logAuditAction(
    fromCrewMemberId,
    fromUser.name,
    'Crew Handover',
    `Handover to ${toUser.name} (${role})${incidentId ? ` - Incident: ${incidentId}` : ''}. Details: ${details || 'N/A'}`,
    'Workforce',
    {
      tenantId,
      targetType: 'CrewHandover',
      targetId: handoverEvent.id,
      metadata: {
        fromCrewMemberId,
        toCrewMemberId,
        role,
        incidentId: incidentId || null,
      },
    }
  );

  console.log(`✓ Handover created: ${fromUser.name} → ${toUser.name}`);
  return handoverEvent;
}

// ─── Shift Queries ────────────────────────────────────────────────────────

/**
 * Get active shift for crew member
 */
export async function getActiveCrewShift(
  tenantId: string,
  crewMemberId: string
): Promise<CrewShift | null> {
  const shifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', crewMemberId),
        where('shiftEnd', '==', null)
      )
    )
  );

  if (shifts.size === 0) {
    return null;
  }

  const doc = shifts.docs[0];
  return {
    ...((doc.data() as CrewShift)),
    id: doc.id,
  };
}

/**
 * Get all active crew shifts for a vehicle
 */
export async function getVehicleCrewShifts(
  tenantId: string,
  vehicleId: string
): Promise<CrewShift[]> {
  const shifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('vehicleId', '==', vehicleId),
        where('shiftEnd', '==', null)
      )
    )
  );

  return shifts.docs.map((doc) => ({
    ...((doc.data() as CrewShift)),
    id: doc.id,
  }));
}

/**
 * Get shift history for crew member
 */
export async function getCrewShiftHistory(
  tenantId: string,
  crewMemberId: string,
  limit: number = 10
): Promise<CrewShift[]> {
  const shifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', crewMemberId)
      )
    )
  );

  return shifts.docs
    .map((doc) => ({
      ...((doc.data() as CrewShift)),
      id: doc.id,
    }))
    .sort(
      (a, b) =>
        new Date(b.shiftStart).getTime() -
        new Date(a.shiftStart).getTime()
    )
    .slice(0, limit);
}

/**
 * Check if crew worked >48 hours this week
 */
export async function checkWeeklyComplianceHours(
  tenantId: string,
  crewMemberId: string
): Promise<{ totalHours: number; compliant: boolean; warning?: string }> {
  // Get shifts from last 7 days
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const shifts = await getDocs(
    query(
      collection(db, 'crew_shifts'),
      and(
        where('tenantId', '==', tenantId),
        where('crewMemberId', '==', crewMemberId)
      )
    )
  );

  let totalMinutes = 0;

  shifts.docs.forEach((doc) => {
    const shift = doc.data() as CrewShift;
    const shiftStart = new Date(shift.shiftStart);
    const shiftEnd = new Date(shift.shiftEnd || now);

    if (shiftStart >= sevenDaysAgo) {
      const durationMinutes = Math.round(
        (shiftEnd.getTime() - shiftStart.getTime()) / 1000 / 60
      );
      totalMinutes += durationMinutes;
    }
  });

  const totalHours = Math.round(totalMinutes / 60);
  const compliant = totalHours <= 48;

  return {
    totalHours,
    compliant,
    warning: !compliant
      ? `Crew member has worked ${totalHours}h this week (limit: 48h)`
      : undefined,
  };
}

/**
 * Check if crew has taken sufficient breaks
 */
export async function checkBreakCompliance(
  shift: CrewShift
): Promise<{ compliant: boolean; message: string }> {
  const shiftDurationMinutes = Math.round(
    (new Date(shift.shiftEnd || new Date()).getTime() -
      new Date(shift.shiftStart).getTime()) /
      1000 /
      60
  );

  // Rule: 20min break per 6h work
  const requiredBreakMinutes = Math.ceil(shiftDurationMinutes / 360) * 20;
  const actualBreakMinutes = shift.totalBreakMinutes || 0;

  const compliant = actualBreakMinutes >= requiredBreakMinutes;

  return {
    compliant,
    message: compliant
      ? `✓ Break compliant: ${actualBreakMinutes}min taken (required: ${requiredBreakMinutes}min)`
      : `⚠️ Break non-compliant: ${actualBreakMinutes}min taken (required: ${requiredBreakMinutes}min)`,
  };
}
